import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Bot,
  Circle,
  Minus,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { fetchPricingAdvice, fetchPricingModel } from "../api/tenders";
import type { PricingCurvePoint, PricingModel, PricingModelFactor } from "../types";

interface Props {
  tenderId: string;
  templateIds: string[];
  /** Multiplier currently set in the pricing draft (%). */
  currentMultiplier: number;
  /** Subtotal the multiplier applies to (100% base, PLN) — for the margin in złoty. */
  baseSubtotal: number;
  onApply: (multiplier: number) => void;
}

const DEFAULT_COST_PCT = 75;
/** Conservative ceiling above the observed bid envelope (mirrors backend `_REC_STEP`, pp). */
const REC_STEP = 10;

const WEIGHT_LABEL: Record<string, string> = { high: "wysoka", medium: "średnia", low: "niska" };
const WEIGHT_COLOR: Record<string, string> = {
  high: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  low: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};
const CONFIDENCE_LABEL: Record<string, string> = { high: "wysoka", medium: "średnia", low: "niska" };

const pctLabel = (v: number) => `${Math.round(v * 100)}%`;
const plnFmt = (v: number) => Math.round(v).toLocaleString("pl-PL") + " zł";

/** Importance badge = the weight the model actually gave the factor, relative to the strongest one
 *  (the rejon carries a 5:1 prior, so it leads whenever we know its segment). Falls back to the raw
 *  measured spread when no factor has any weight (nothing known about this tender's segments). */
function weightBucket(factor: PricingModelFactor, maxWeight: number): "high" | "medium" | "low" {
  if (maxWeight > 0) {
    const rel = (factor.weight ?? 0) / maxWeight;
    if (rel >= 0.5) return "high";
    if (rel >= 0.2) return "medium";
    return "low";
  }
  if (factor.portfolio_spread >= 0.25) return "high";
  if (factor.portfolio_spread >= 0.12) return "medium";
  return "low";
}

/** Linear interpolation of win probability at an arbitrary multiplier along the fitted curve. */
function winProbAt(curve: PricingCurvePoint[], m: number): number {
  if (!curve.length) return 0;
  const lo = curve[0];
  const hi = curve[curve.length - 1];
  if (m <= lo.multiplier) return lo.win_prob;
  if (m >= hi.multiplier) return hi.win_prob;
  for (let i = 1; i < curve.length; i++) {
    if (m <= curve[i].multiplier) {
      const a = curve[i - 1];
      const b = curve[i];
      const t = (m - a.multiplier) / (b.multiplier - a.multiplier);
      return a.win_prob + t * (b.win_prob - a.win_prob);
    }
  }
  return hi.win_prob;
}

interface CurvePointRow {
  m: number;
  win: number;
  exp: number;
}

function CurveTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: CurvePointRow }> }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
      <div>Mnożnik {p.m}%</div>
      <div>Szansa {Math.round(p.win * 100)}%</div>
      <div>Oczek. marża {plnFmt(p.exp)}</div>
    </div>
  );
}

function FactorArrow({ sentiment }: { sentiment: "up" | "down" | "neutral" }) {
  if (sentiment === "up") return <ArrowUp size={14} className="text-green-600 dark:text-green-400" />;
  if (sentiment === "down") return <ArrowDown size={14} className="text-red-500" />;
  return <Circle size={9} className="text-gray-400 fill-gray-400" />;
}

export function PricingHint({ tenderId, templateIds, currentMultiplier, baseSubtotal, onApply }: Props) {
  const [costPct, setCostPct] = useState(DEFAULT_COST_PCT);

  // win-probability curve depends only on the tender (portfolio fit + segment shift). Margins are
  // recomputed client-side from cost/base so the cost slider is instant (no refetch).
  const { data, isLoading } = useQuery({
    queryKey: ["pricing-model", tenderId],
    queryFn: () => fetchPricingModel(tenderId),
    staleTime: 5 * 60_000,
  });
  const model: PricingModel | null = data?.model ?? null;

  const advice = useMutation({ mutationFn: fetchPricingAdvice });

  // Expected-margin curve + recommendation + current, all from the win_prob curve and current
  // cost/base. The recommendation is capped to the observed bid envelope (+REC_STEP) so it stays
  // incremental relative to where we actually bid, never the extrapolated margin peak.
  const derived = useMemo(() => {
    if (!model || !model.curve.length) return null;
    const margin = (m: number) => (baseSubtotal * (m - costPct)) / 100;
    const points = model.curve.map((c) => ({
      m: c.multiplier,
      winPct: c.win_prob * 100,
      win: c.win_prob,
      exp: c.win_prob * margin(c.multiplier),
    }));
    const best = (ps: typeof points) => ps.reduce((a, b) => (b.exp > a.exp ? b : a), ps[0]);
    const rawOptimum = best(points);
    const ceiling = model.observed_hi != null ? model.observed_hi + REC_STEP : Infinity;
    const eligible = points.filter((p) => p.m <= ceiling);
    const recommended = best(eligible.length ? eligible : points);
    const capped = rawOptimum.m > ceiling;
    const curP = winProbAt(model.curve, currentMultiplier);
    const current = { m: currentMultiplier, win: curP, exp: curP * margin(currentMultiplier) };
    return { points, recommended, capped, current, margin };
  }, [model, costPct, baseSubtotal, currentMultiplier]);

  if (templateIds.length === 0 || isLoading) return null;
  if (!model || !derived) return null; // non-ZE tender or no data

  const { points, recommended, current } = derived;
  const maxFactorWeight = model.factors.reduce((a, f) => Math.max(a, f.weight ?? 0), 0);
  const delta = recommended.m - currentMultiplier;
  const dir: "up" | "down" | "hold" =
    Math.abs(delta) < 2.5 ? "hold" : delta > 0 ? "up" : "down";
  const DirIcon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  const dirColor =
    dir === "up"
      ? "text-green-600 dark:text-green-400"
      : dir === "down"
        ? "text-orange-500"
        : "text-gray-500 dark:text-gray-400";
  const dirWord = dir === "up" ? "podnieś" : dir === "down" ? "obniż" : "utrzymaj";
  const cMin = model.curve_min ?? 90;
  const cMax = model.curve_max ?? 150;
  const clampedCurrent = Math.min(cMax, Math.max(cMin, currentMultiplier));
  const axisTicks: number[] = [];
  for (let t = Math.ceil(cMin / 10) * 10; t <= cMax; t += 10) axisTicks.push(t);
  const entry = model.entry_threshold;
  const obsHi = model.observed_hi;

  return (
    <div className="rounded-lg border border-blue-100 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--color-primary)] dark:text-blue-300">
          <Sparkles size={15} />
          Wartość oczekiwana wyceny
        </div>
        <span className="text-[11px] text-gray-500 dark:text-gray-400">
          model na {model.overall_won} z {model.overall_total} rozstrzygniętych
        </span>
      </div>

      {model.low_confidence && (
        <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
          Mała próba do dopasowania ({model.fit_n}) — traktuj krzywą orientacyjnie / jako ryzykowną grę.
        </p>
      )}

      {/* Headline tiles for the current multiplier */}
      <div className="mt-2 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-md bg-white/70 dark:bg-gray-800/60 px-2 py-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">Mnożnik</div>
          <div className="text-lg font-bold font-mono text-gray-900 dark:text-gray-100">{currentMultiplier}%</div>
        </div>
        <div className="rounded-md bg-white/70 dark:bg-gray-800/60 px-2 py-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">Szansa wygranej</div>
          <div className="text-lg font-bold font-mono text-gray-900 dark:text-gray-100">{pctLabel(current.win)}</div>
        </div>
        <div className="rounded-md bg-white/70 dark:bg-gray-800/60 px-2 py-2">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">Oczek. marża</div>
          <div className={`text-lg font-bold font-mono ${current.exp >= 0 ? "text-green-600 dark:text-green-400" : "text-orange-500"}`}>
            {plnFmt(current.exp)}
          </div>
        </div>
      </div>

      {/* Win-probability curve vs multiplier (90–150% of base offer value) */}
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
          Szansa wygranej wg mnożnika (% wartości bazowej)
        </div>
        <ResponsiveContainer width="100%" height={170}>
          <LineChart data={points} margin={{ top: 6, right: 10, left: 0, bottom: 2 }}>
            <CartesianGrid vertical={false} stroke="rgba(130,130,130,0.18)" />
            <XAxis
              dataKey="m"
              type="number"
              domain={[cMin, cMax]}
              ticks={axisTicks}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 10, fill: "#898781" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 1]}
              tickFormatter={(v) => `${Math.round(v * 100)}%`}
              tick={{ fontSize: 10, fill: "#898781" }}
              axisLine={false}
              tickLine={false}
              width={34}
            />
            <Tooltip content={<CurveTooltip />} />
            {/* Above the observed bid envelope the curve is extrapolation — shade it as untested. */}
            {obsHi != null && obsHi < cMax && (
              <ReferenceArea x1={obsHi} x2={cMax} fill="rgba(210,59,59,0.07)" stroke="none"
                label={{ value: "nieprzetestowane", fontSize: 8, fill: "#d03b3b", position: "insideTop" }} />
            )}
            {entry != null && entry >= cMin && entry <= cMax && (
              <ReferenceLine x={entry} stroke="#898781" strokeDasharray="2 2"
                label={{ value: "typowo", fontSize: 9, fill: "#898781", position: "top" }} />
            )}
            <ReferenceLine x={clampedCurrent} stroke="#2a78d6" strokeDasharray="3 3"
              label={{ value: "teraz", fontSize: 9, fill: "#2a78d6", position: "top" }} />
            <ReferenceLine x={recommended.m} stroke="#0ca30c"
              label={{ value: "rekom.", fontSize: 9, fill: "#0ca30c", position: "top" }} />
            <Line dataKey="win" stroke="#2a78d6" strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
        {entry != null && (
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Zwykle startujecie ~<b>{entry}%</b> na takim profilu
            {model.entry_basis
              ? ` (${model.entry_basis.factor}: ${model.entry_basis.segment}, ${model.entry_basis.segment_won}/${model.entry_basis.segment_n} wygranych)`
              : ""}
            {obsHi != null ? ` · powyżej ~${obsHi}% to teren nieprzetestowany` : ""}.
          </p>
        )}
      </div>

      {/* Recommendation */}
      <div className="mt-1 flex items-center gap-2 flex-wrap text-sm text-gray-700 dark:text-gray-200">
        <DirIcon size={16} className={dirColor} />
        {dir === "hold" ? (
          <span>Twój mnożnik jest blisko rekomendacji (~{recommended.m}%) w granicach obserwowanych ofert.</span>
        ) : (
          <span>
            <b className={dirColor}>{dirWord}</b> w okolice <b>{recommended.m}%</b> → oczek. marża{" "}
            <b className={recommended.exp >= 0 ? "text-green-600 dark:text-green-400" : "text-orange-500"}>
              {plnFmt(recommended.exp)}
            </b>{" "}
            (szansa {pctLabel(recommended.win)}).
          </span>
        )}
        {dir !== "hold" && (
          <button
            type="button"
            onClick={() => onApply(recommended.m)}
            className="text-xs font-semibold rounded-md bg-[var(--color-primary)] text-white px-2.5 py-1 hover:opacity-90 transition"
          >
            Zastosuj {recommended.m}%
          </button>
        )}
      </div>
      {derived.capped && (
        <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
          Rekomendacja przycięta do granicy obserwowanych ofert — wyżej model nie ma danych i szansa
          gwałtownie spada. Podbijaj przyrostowo, nie skokowo.
        </p>
      )}

      {/* Hard factors with sentiment — rejon first, then oddział (backend-sorted by model weight) */}
      <div className="mt-2.5 flex flex-col gap-1">
        <div className="text-[10px] uppercase tracking-wide text-gray-400">Czynniki (wpływ na szansę)</div>
        {model.factors.map((f) => {
          const w = weightBucket(f, maxFactorWeight);
          return (
            <div key={f.factor} className="flex items-center gap-2 text-xs">
              <FactorArrow sentiment={f.sentiment} />
              <span className="text-gray-700 dark:text-gray-200">
                <b>{f.factor}</b>
                {f.segment ? `: ${f.segment}` : ""}
              </span>
              <span className="text-gray-500 dark:text-gray-400 font-mono">
                {f.segment_win_rate != null
                  ? `${pctLabel(f.segment_win_rate)} (${f.segment_won}/${f.segment_n})`
                  : "brak danych"}
              </span>
              {f.segment_median_multiplier != null && (
                <span className="text-[10px] text-gray-400 font-mono">start ~{f.segment_median_multiplier}%</span>
              )}
              {f.low_sample && f.segment_n > 0 && (
                <span className="text-[10px] text-amber-500">mała próba</span>
              )}
              <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase ${WEIGHT_COLOR[w]}`}>
                waga {WEIGHT_LABEL[w]}
              </span>
            </div>
          );
        })}
      </div>

      {/* Cost input — recomputes margins instantly */}
      <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
        <label>Koszty wewn.</label>
        <input
          type="number"
          min={0}
          max={100}
          value={costPct}
          onChange={(e) => setCostPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
          className="w-14 text-center border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <span>% wartości bazowej</span>
        <span className="text-gray-400">oczek. marża = szansa × (mnożnik − koszty) × podstawa</span>
      </div>

      {/* AI explanation */}
      <div className="mt-3 border-t border-blue-100 dark:border-blue-800 pt-2">
        {!advice.data?.advice && (
          <button
            type="button"
            disabled={advice.isPending}
            onClick={() =>
              advice.mutate({
                tender_id: tenderId,
                cost_pct: costPct,
                base_subtotal: Math.round(baseSubtotal),
                current_multiplier: currentMultiplier,
              })
            }
            className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-primary)] dark:text-blue-400 hover:opacity-80 transition disabled:opacity-50"
          >
            <Bot size={14} />
            {advice.isPending ? "AI analizuje…" : "Wyjaśnij ocenę (AI)"}
          </button>
        )}

        {advice.isError && <p className="text-xs text-red-500">Nie udało się pobrać wyjaśnienia AI.</p>}
        {advice.data && !advice.data.available && (
          <p className="text-xs text-gray-400">Wyjaśnienie AI niedostępne (brak klucza API).</p>
        )}

        {advice.data?.advice && (
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-1.5">
              <Bot size={14} className="mt-0.5 text-[var(--color-primary)] flex-shrink-0" />
              <div className="text-xs font-medium text-gray-800 dark:text-gray-100">{advice.data.advice.headline}</div>
            </div>
            <ul className="ml-5 list-disc flex flex-col gap-0.5 text-xs text-gray-600 dark:text-gray-300">
              {advice.data.advice.reasoning.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
            {advice.data.advice.factors.length > 0 && (
              <div className="ml-5 flex flex-col gap-1">
                <div className="text-[10px] uppercase tracking-wide text-gray-400">Czynniki oceny</div>
                {advice.data.advice.factors.map((f, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-xs">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase ${WEIGHT_COLOR[f.weight] ?? WEIGHT_COLOR.low}`}>
                      {WEIGHT_LABEL[f.weight] ?? f.weight}
                    </span>
                    <span className="text-gray-700 dark:text-gray-200">
                      <b>{f.name}</b> — {f.effect}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="ml-5 text-[11px] text-gray-400">
              Pewność oceny: {CONFIDENCE_LABEL[advice.data.advice.confidence] ?? advice.data.advice.confidence}
              {advice.data.advice.caveat ? ` · ${advice.data.advice.caveat}` : ""}
            </div>
            {advice.data.cost_usd != null && (
              <div className="ml-5 text-[11px] text-gray-400">
                Koszt analizy AI: ${Number(advice.data.cost_usd).toFixed(4)}
                {advice.data.tokens
                  ? ` · ${(advice.data.tokens.input + advice.data.tokens.output).toLocaleString("pl-PL")} tok.`
                  : ""}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
